'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTheme } from 'next-themes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { signOut, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { ChevronLeft, ChevronRight, Moon, Sun, SunMoon, Menu } from 'lucide-react';
import Timer from '../timer/Timer';
import { useQuery } from '@tanstack/react-query';
import { normalizeLocaleCode, pickBestLocale, useI18n } from '@/i18n';
import { useToast } from '@/components/ui/use-toast';
import NotificationBell from '@/components/notifications/NotificationBell';

interface HeaderProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  pageTitle?: string;
}

export default function Header({
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  pageTitle,
}: HeaderProps) {
  const { locale, setLocale, t, generationStatus, aiPreparing, aiError, requestAiFill } = useI18n();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoSrc, setLogoSrc] = useState('/images/UAlogo-light.png');
  const { data: session, update } = useSession();
  const { toast } = useToast();
  const [prepareRequested, setPrepareRequested] = useState(false);
  // TECH DEBT: Header currently ignores session.user.modules; module-based
  // filtering will be added when modules are stored in the DB.
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [languageSearch, setLanguageSearch] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const languageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setLogoSrc(
      resolvedTheme === 'dark'
        ? '/images/UAlogo-dark.png'
        : '/images/UAlogo-light.png',
    );
  }, [resolvedTheme]);

  const normalizeParam = (value: string | string[] | undefined) =>
    Array.isArray(value) ? value[0] : value;

  const pageTitles = useMemo(
    () => ({
      '/ua/dashboard': t('layout.header.pageTitle.uaDashboard'),
      '/projects': t('layout.header.pageTitle.projects'),
      '/type-of-loss': t('layout.header.pageTitle.typeOfLoss'),
      '/hourly-rate': t('layout.header.pageTitle.hourlyRate'),
      '/invoice-activity': t('layout.header.pageTitle.invoiceCenter'),
      '/invoice-center': t('layout.header.pageTitle.invoiceCenter'),
      '/adr/dashboard': t('layout.header.pageTitle.adrDashboard'),
      '/adr/projects': t('layout.header.pageTitle.adrProjects'),
      '/adr/type-of-loss': t('layout.header.pageTitle.adrTypeOfLoss'),
      '/cr/dashboard': t('layout.header.pageTitle.caseReadyDashboard'),
      '/cr/type-of-loss': t('layout.header.pageTitle.caseReadyTypeOfLoss'),
      '/sf/dashboard': t('layout.header.pageTitle.settleFastDashboard'),
      '/sf/projects': t('layout.header.pageTitle.settleFastProjects'),
      '/sf/type-of-loss': t('layout.header.pageTitle.settleFastTypeOfLoss'),
      '/admin/companies': t('layout.header.pageTitle.companies'),
      '/admin/settings': t('layout.header.pageTitle.settings'),
      '/admin/users': t('layout.header.pageTitle.users'),
      '/admin/pending-approvals': t('layout.header.pageTitle.pendingApprovals'),
      '/admin/invoice-settings': t('layout.header.pageTitle.generalInvoiceSettings'),
    }),
    [t],
  );

  const moduleValue = normalizeParam(router.query?.module);

  let moduleForTimer: 'UA' | 'ADR' | 'CR' | 'SettleFast' | undefined;
  if (moduleValue === 'UA' || moduleValue === 'ADR' || moduleValue === 'CR' || moduleValue === 'SettleFast') {
    moduleForTimer = moduleValue;
  } else if (router.pathname.startsWith('/adr')) {
    moduleForTimer = 'ADR';
  } else if (router.pathname.startsWith('/cr')) {
    moduleForTimer = 'CR';
  } else if (router.pathname.startsWith('/sf') || router.pathname.startsWith('/settlefast')) {
    moduleForTimer = 'SettleFast';
  } else if (
    router.pathname.startsWith('/ua') ||
    router.pathname.startsWith('/projects') ||
    router.pathname.startsWith('/projectsv2') ||
    router.pathname.startsWith('/projects-v2')
  ) {
    moduleForTimer = 'UA';
  }

  let sectionPrefix = '';

  if (moduleValue === 'UA') {
    sectionPrefix = t('layout.header.section.ua');
  } else if (moduleValue === 'ADR' || moduleValue === 'CR') {
    sectionPrefix = t('layout.header.section.adr');
  } else if (moduleValue === 'SettleFast' || moduleValue === 'SF') {
    sectionPrefix = t('layout.header.section.settleFast');
  } else if (router.pathname.startsWith('/ua')) {
    sectionPrefix = t('layout.header.section.ua');
  } else if (router.pathname.startsWith('/adr') || router.pathname.startsWith('/cr')) {
    sectionPrefix = t('layout.header.section.adr');
  } else if (router.pathname.startsWith('/sf')) {
    sectionPrefix = t('layout.header.section.settleFast');
  } else if (
    router.pathname.startsWith('/admin/users') ||
    router.pathname.startsWith('/admin/i18n') ||
    router.pathname === '/superuser' ||
    router.pathname.startsWith('/admin/companies') ||
    router.pathname.startsWith('/admin/billing') ||
    router.pathname.startsWith('/admin/pending-approvals')
  ) {
    sectionPrefix = t('layout.header.section.globalAdmin');
  } else if (
    router.pathname.startsWith('/admin') ||
    router.pathname.startsWith('/support')
  ) {
    sectionPrefix = t('layout.header.section.admin');
  }

  const isProjectPath =
    router.pathname.startsWith('/projects/') ||
    router.pathname.startsWith('/adr/projects/') ||
    router.pathname.startsWith('/cr/projects/') ||
    router.pathname.startsWith('/sf/projects/') ||
    router.pathname.startsWith('/settlefast/projects/') ||
    router.pathname.startsWith('/projectsv2/projects/') ||
    router.pathname.startsWith('/projects-v2/');

  const showTimer =
    (moduleForTimer === 'UA' ||
      moduleForTimer === 'ADR' ||
      moduleForTimer === 'SettleFast') &&
    isProjectPath;

  const baseTitle = pageTitle || pageTitles[router.pathname] || '';

  const currentPageTitle =
    sectionPrefix && baseTitle ? `${sectionPrefix} | ${baseTitle}` : baseTitle;
  const userName = session?.user?.name || '';
  const canManageCompanyBilling = Boolean(session?.user?.canManageCompanyBilling);
  const isSuperAdmin =
    session?.user?.isSuperAdmin === true || session?.user?.role === 'superadmin';
  const isCompanyAdmin = Boolean(session?.user?.isCompanyAdmin);
  const showCompanyAdminLinks = isSuperAdmin || isCompanyAdmin;

  const preservedModuleSideQuery = useMemo(() => {
    const searchParams = new URLSearchParams();
    const moduleParam = router.query?.module;
    const sideParam = router.query?.side;

    const moduleValue = normalizeParam(moduleParam);
    if (moduleValue) {
      searchParams.set('module', moduleValue);
    }

    const sideValue = normalizeParam(sideParam);
    if (sideValue) {
      searchParams.set('side', sideValue);
    }

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
  }, [router.query?.module, router.query?.side]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (currentPageTitle) {
      document.title = `${t('layout.header.appName')} > ${currentPageTitle}`;
    } else {
      document.title = t('layout.header.appName');
    }
  }, [currentPageTitle, t]);

  useEffect(() => {
    if (!languageOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!languageRef.current) return;
      if (!languageRef.current.contains(event.target as Node)) {
        setLanguageOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [languageOpen]);

  useEffect(() => {
    if (!languageOpen) {
      setLanguageSearch('');
    }
  }, [languageOpen]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setCatalogSearch(languageSearch.trim());
    }, 200);
    return () => {
      window.clearTimeout(handle);
    };
  }, [languageSearch]);

  const cycleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  const fallbackLocaleOptions = [
    { code: 'en', label: t('layout.language.english') },
    { code: 'es', label: t('layout.language.spanish') },
  ];

  const localesQuery = useQuery({
    queryKey: ['i18n-locales'],
    queryFn: async () => {
      const res = await fetch('/api/i18n/locales');
      if (!res.ok) {
        throw new Error(t('layout.language.loadLocalesError'));
      }
      const data = (await res.json()) as {
        ok?: boolean;
        locales?: Array<{ code: string; displayName?: string | null; nativeName?: string | null }>;
      };
      return data?.locales ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const formatInstalledLocales = (
    entries: Array<{ code: string; displayName?: string | null; nativeName?: string | null }>,
  ) => {
    const seen = new Set<string>();
    const normalized = entries
      .map((entry) => {
        const code = String(entry.code || '').trim();
        const normalizedCode = normalizeLocaleCode(code);
        if (!code) return null;
        const label = entry.nativeName || entry.displayName || normalizedCode;
        return { code: normalizedCode, label };
      })
      .filter(Boolean) as Array<{ code: string; label: string }>;

    const deduped: Array<{ code: string; label: string }> = [];
    for (const option of normalized) {
      if (seen.has(option.code)) continue;
      seen.add(option.code);
      deduped.push(option);
    }
    return deduped.length ? deduped : fallbackLocaleOptions;
  };

  const localeOptions = useMemo(() => {
    if (localesQuery.isError || localesQuery.isLoading) {
      return fallbackLocaleOptions;
    }
    return formatInstalledLocales(localesQuery.data || []);
  }, [fallbackLocaleOptions, localesQuery.data, localesQuery.isError, localesQuery.isLoading]);

  const catalogQuery = useQuery({
    queryKey: ['i18n-catalog', catalogSearch],
    queryFn: async () => {
      const searchParam = catalogSearch ? `?q=${encodeURIComponent(catalogSearch)}` : '';
      const res = await fetch(`/api/i18n/catalog${searchParam}`);
      if (!res.ok) {
        throw new Error(t('layout.language.loadCatalogError'));
      }
      const data = (await res.json()) as {
        ok?: boolean;
        locales?: Array<{
          code: string;
          englishName?: string | null;
          nativeName?: string | null;
          isRtl?: boolean;
          isCommon?: boolean;
        }>;
      };
      return data?.locales ?? [];
    },
    enabled: Boolean(catalogSearch),
    staleTime: 30 * 1000,
  });

  const handleLocaleChange = async (nextLocale: string, availableCodes?: string[]) => {
    const normalized = pickBestLocale(
      nextLocale,
      availableCodes ?? localeOptions.map((option) => option.code),
      'en',
    );
    setLocale(normalized);
    setLanguageOpen(false);
    setLanguageSearch('');

    try {
      const profileRes = await fetch('/api/profile/me', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const profileData = await profileRes.json().catch(() => null);
      if (!profileRes.ok || !profileData?.ok) {
        return;
      }
      const firstName = profileData.profile?.firstName ?? profileData.user?.first_name ?? '';
      const lastName = profileData.profile?.lastName ?? profileData.user?.last_name ?? '';
      if (!firstName || !lastName) return;
      const timezone = profileData.user?.timezone ?? null;
      const displayName = profileData.user?.name ?? session?.user?.name ?? '';

      const saveRes = await fetch('/api/profile/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          timezone,
          preferred_locale: normalized,
        }),
      });
      const saveData = await saveRes.json().catch(() => null);
      if (!saveRes.ok || !saveData?.ok) return;
      await update({
        name: displayName,
        timezone,
        preferred_locale: normalized,
      });
    } catch {
      // ignore profile update errors
    }
  };

  const showPrepareButton =
    locale !== 'en' && isSuperAdmin && (generationStatus?.missingKeys ?? 0) > 0;

  const activeLocaleLabel =
    localeOptions.find((option) => option.code === locale)?.label || locale;

  const filteredLocaleOptions = useMemo(() => {
    if (!catalogSearch) return localeOptions;
    return (catalogQuery.data || []).map((entry) => {
      const code = normalizeLocaleCode(entry.code);
      const english = entry.englishName ? String(entry.englishName) : '';
      const native = entry.nativeName ? String(entry.nativeName) : '';
      let label = native || english || code;
      if (native && english && native !== english) {
        label = `${native} (${english})`;
      }
      return { code, label };
    });
  }, [catalogQuery.data, catalogSearch, localeOptions]);

  const dbInstalledLocaleCodes = useMemo(() => {
    const entries = localesQuery.data ?? [];
    return new Set(entries.map((entry) => String(entry.code || '').toLowerCase().trim()));
  }, [localesQuery.data]);

  const handleCatalogSelect = async (nextLocale: string) => {
    const normalized = normalizeLocaleCode(nextLocale);
    if (!normalized) return;
    if (normalized === 'en') {
      await handleLocaleChange(normalized);
      return;
    }

    if (!dbInstalledLocaleCodes.has(normalized)) {
      try {
        const installRes = await fetch('/api/i18n/install-locale', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ locale: normalized }),
        });
        const installData = (await installRes.json().catch(() => null)) as {
          ok?: boolean;
          locale?: string;
        } | null;
        if (!installRes.ok || !installData?.ok || !installData.locale) {
          throw new Error(t('layout.language.loadLocalesError'));
        }
        const installedCode = normalizeLocaleCode(installData.locale);
        const refetchResult = await localesQuery.refetch();
        const refreshedOptions = formatInstalledLocales(refetchResult.data || []);
        await handleLocaleChange(
          installedCode || normalized,
          refreshedOptions.map((option) => option.code),
        );
        return;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('layout.language.loadLocalesError');
        toast({
          title: t('layout.language.loadLocalesError'),
          description: message,
          variant: 'destructive',
        });
        return;
      }
    }

    await handleLocaleChange(normalized);
  };

  const handlePrepareClick = async () => {
    setPrepareRequested(true);
    const result = await requestAiFill(locale);
    if (!result.ok) {
      setPrepareRequested(false);
      toast({
        title: t('layout.header.prepareFailed'),
        description: result.error || t('layout.header.prepareFailed'),
        variant: 'destructive',
      });
      return;
    }
    toast({ description: t('layout.header.preparingTranslations') });
  };

  useEffect(() => {
    if (!prepareRequested) return;
    if (aiPreparing) return;
    if (aiError) {
      toast({
        title: t('layout.header.prepareFailed'),
        description: aiError,
        variant: 'destructive',
      });
      setPrepareRequested(false);
      return;
    }
    if (generationStatus?.missingKeys === 0) {
      toast({ title: t('layout.header.languageReady') });
      setPrepareRequested(false);
    }
  }, [aiError, aiPreparing, generationStatus?.missingKeys, prepareRequested, t, toast]);

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-white to-[#00A0DC] dark:from-[#4B5563] dark:to-[#1E3A8A] text-neutral-900 dark:text-neutral-100 transition-colors duration-300 ease-in-out">
      <div className="grid grid-cols-[auto,minmax(0,1fr),auto] items-center h-12 px-3 sm:h-16 sm:px-4 md:h-16 md:px-6 border-b border-neutral-200 dark:border-neutral-800">
        <div className="flex items-center">
          <Image
            src={logoSrc}
            alt={t('layout.header.logoAlt')}
            width={96}
            height={96}
            className="object-contain h-8 sm:h-10 md:h-12 w-auto"
          />
        </div>

        <div className="min-w-0">
          {currentPageTitle && (
            <h1 className="hidden sm:block w-full text-center text-xl font-semibold text-gray-900 dark:text-white truncate whitespace-nowrap overflow-hidden">
              {currentPageTitle}
            </h1>
          )}
        </div>

        <div className="flex items-center justify-end flex-shrink-0">
          <div className="flex items-center">
            {showTimer && <Timer />}
            <div className="relative ml-1 sm:ml-2" ref={languageRef}>
              <button
                type="button"
                onClick={() => setLanguageOpen((prev) => !prev)}
                className="rounded border border-white/60 bg-white/80 px-2 py-1 text-xs font-medium text-gray-900 shadow-sm transition hover:bg-white dark:border-white/10 dark:bg-gray-800 dark:text-gray-100"
                aria-label={t('layout.header.language')}
                title={t('layout.header.language')}
                aria-expanded={languageOpen}
              >
                {activeLocaleLabel}
              </button>
              {languageOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md border border-gray-200 bg-white p-2 text-gray-900 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                  <input
                    type="text"
                    value={languageSearch}
                    onChange={(event) => setLanguageSearch(event.target.value)}
                    placeholder={t('layout.language.searchPlaceholder')}
                    className="mb-2 w-full rounded border border-gray-200 px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                    autoFocus
                  />
                  <div className="max-h-48 overflow-y-auto">
                    {filteredLocaleOptions.length ? (
                      filteredLocaleOptions.map((option) => (
                        <button
                          key={option.code}
                          type="button"
                          onClick={() => handleCatalogSelect(option.code)}
                          className={`flex w-full flex-col items-start rounded px-2 py-1 text-left text-xs transition hover:bg-gray-100 dark:hover:bg-gray-800 ${
                            option.code === locale
                              ? 'font-semibold text-blue-600 dark:text-blue-300'
                              : ''
                          }`}
                        >
                          <span>{option.label}</span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {option.code}
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
                        {catalogSearch && catalogQuery.isLoading
                          ? t('common.state.loading')
                          : t('common.state.noResults')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {showPrepareButton && (
              <button
                type="button"
                onClick={handlePrepareClick}
                disabled={aiPreparing}
                className="ml-1 sm:ml-2 inline-flex items-center gap-2 rounded border border-white/60 bg-white/80 px-2 py-1 text-xs font-medium text-gray-900 shadow-sm transition hover:bg-white disabled:opacity-70 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100"
              >
                {aiPreparing && (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
                )}
                {t('layout.header.prepareLanguage')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className={`ml-1 sm:ml-2 flex items-center justify-center p-2 rounded hover:bg-white dark:hover:bg-gray-800 hover:scale-105 transition-transform transition-colors cursor-pointer focus:outline-none ${
                isSidebarOpen ? 'inline-flex' : 'hidden'
              } md:inline-flex`}
              aria-label={
                isSidebarCollapsed
                  ? t('layout.sidebar.expand')
                  : t('layout.sidebar.collapse')
              }
              title={
                isSidebarCollapsed
                  ? t('layout.sidebar.expand')
                  : t('layout.sidebar.collapse')
              }
            >
              {isSidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
            {/* TECH DEBT: Basic hover styling for theme toggle; replace with design system component later. */}
            <button
              onClick={() => cycleTheme()}
              className="flex items-center justify-center p-2 rounded hover:bg-white dark:hover:bg-gray-800 hover:scale-105 transition-transform transition-colors cursor-pointer focus:outline-none"
              aria-label={t('layout.header.toggleTheme')}
            >
              {mounted && (
                theme === 'system' || !theme ? (
                  <SunMoon className="w-5 h-5" />
                ) : theme === 'dark' ? (
                  <Moon className="w-5 h-5" />
                ) : (
                  <Sun className="w-5 h-5" />
                )
              )}
            </button>
            <NotificationBell />

            {/* User menu */}
            <div className="relative ml-1 sm:ml-2">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-1.5 sm:p-2 rounded transition-colors duration-200 ease-in-out hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-5 h-5 sm:w-6 sm:h-6"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </button>

            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 rounded-md ring-1 ring-black ring-opacity-5 bg-white text-gray-900 shadow-md dark:bg-[#1F2937] dark:text-gray-100 dark:shadow-lg z-50">
                <div className="py-1 space-y-1">
                  {session?.user && (
                    <Link
                      href={
                        canManageCompanyBilling
                          ? `/admin/company-billing${preservedModuleSideQuery}`
                          : `/account/billing${preservedModuleSideQuery}`
                      }
                      onClick={() => setMenuOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 bg-transparent rounded-md transition-colors duration-200 ease-in-out hover:bg-gray-200 hover:text-black dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                    >
                      {canManageCompanyBilling
                        ? t('layout.menu.companySubscriptionBilling')
                        : t('layout.menu.subscriptionBilling')}
                    </Link>
                  )}
                  {showCompanyAdminLinks && (
                    <>
                      <div className="px-4 pt-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                        {t('layout.menu.companyAdmin')}
                      </div>
                      <Link
                        href="/admin/invoice-settings"
                        onClick={() => setMenuOpen(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-900 bg-transparent rounded-md transition-colors duration-200 ease-in-out hover:bg-gray-200 hover:text-black dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                      >
                        {t('layout.menu.generalInvoiceSettings')}
                      </Link>
                    </>
                  )}
                  <Link
                    href={`/support${preservedModuleSideQuery}`}
                    onClick={() => setMenuOpen(false)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-900 bg-transparent rounded-md transition-colors duration-200 ease-in-out hover:bg-gray-200 hover:text-black dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                  >
                    {t('layout.support.support')}
                  </Link>
                  <Link
                    href={`/feedback${preservedModuleSideQuery}`}
                    onClick={() => setMenuOpen(false)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-900 bg-transparent rounded-md transition-colors duration-200 ease-in-out hover:bg-gray-200 hover:text-black dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                  >
                    {t('layout.support.feedback')}
                  </Link>
                  <button
                    onClick={async () => {
                      try {
                        await fetch('/api/auth/logout', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                        });
                      } catch {
                        // ignore logout errors
                      }
                      await signOut({ redirect: false });
                      router.replace('/login');
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-900 bg-transparent rounded-md transition-colors duration-200 ease-in-out hover:bg-gray-200 hover:text-black dark:text-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-neutral-900"
                  >
                    {t('layout.menu.signOut')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {userName && (
          <span className="hidden sm:block ml-2 text-neutral-700 dark:text-neutral-200 font-medium text-sm sm:text-base">
            {userName}
          </span>
        )}

        {/* Mobile menu toggle */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="ml-1 sm:ml-2 md:hidden text-gray-900 dark:text-gray-100"
          aria-label={t('layout.menu.toggleMenu')}
        >
          <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>
      </div>
    </header>
  );
}
